import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { clipboardFiles } from "../hooks/useFileDrop";
import { Markdown } from "./Markdown";
import { ErrorBoundary, ReloadNotice } from "./ErrorBoundary";
import type { MarkdownEditorHandle } from "./MarkdownEditor";

const MarkdownEditor = lazy(() =>
  import("./MarkdownEditor").then((m) => ({ default: m.MarkdownEditor })),
);

interface MarkdownFieldProps {
  value: string;
  placeholder?: string;
  canEdit: boolean;
  onCommit: (value: string) => void;
  /** Called when files are pasted while editing (attach, don't inline). */
  onPasteFiles?: (files: FileList) => void;
}

/**
 * A click-to-edit markdown field: renders the value as markdown, and (when
 * editable) opens a WYSIWYG editor (MDXEditor) on click. Commits on the Save
 * button or Cmd/Ctrl+Enter; Escape cancels. If MDXEditor can't represent the
 * stored markdown, it falls back to a raw-markdown textarea so nothing is lost.
 */
export function MarkdownField({
  value,
  placeholder = "Add a description…",
  canEdit,
  onCommit,
  onPasteFiles,
}: MarkdownFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // Set once MDXEditor reports it can't represent `value` — drop to the textarea.
  const [rawFallback, setRawFallback] = useState(false);
  // A real user edit happened (not MDXEditor's cosmetic load-time normalization).
  const dirty = useRef(false);
  const editorRef = useRef<MarkdownEditorHandle>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (editing && rawFallback) textareaRef.current?.focus();
  }, [editing, rawFallback]);

  if (!editing) {
    return (
      <div
        role={canEdit ? "button" : undefined}
        tabIndex={canEdit ? 0 : undefined}
        onClick={() => {
          if (!canEdit) return;
          startEditing();
        }}
        onKeyDown={(e) => {
          if (canEdit && e.key === "Enter") startEditing();
        }}
        className={`themed-scroll max-h-80 overflow-y-auto rounded px-1 py-0.5 text-sm ${
          canEdit ? "cursor-text hover:bg-slate-100" : ""
        }`}
      >
        {value ? (
          <Markdown>{value}</Markdown>
        ) : (
          <span className="italic text-slate-400">{placeholder}</span>
        )}
      </div>
    );
  }

  function startEditing() {
    setDraft(value);
    dirty.current = false;
    setRawFallback(false);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setDraft(value);
  }

  function commit() {
    // Read the latest content synchronously from whichever editor is mounted.
    const latest = rawFallback
      ? draft
      : (editorRef.current?.getMarkdown() ?? draft);
    setEditing(false);
    // No real edit → never rewrite the stored value (MDXEditor's load-time
    // normalization must not be mistaken for a change).
    if (!rawFallback && !dirty.current) return;
    const next = latest.trim();
    if (next !== value) onCommit(next);
  }

  // Cmd/Ctrl+Enter commits, Escape cancels. Capture phase because MDXEditor /
  // CodeMirror own the keydowns and won't bubble them to us; stopPropagation on
  // Escape keeps the surrounding card Modal from also closing.
  function onKeyDownCapture(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      cancel();
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      commit();
    }
  }

  // Turn a pasted file into an attachment — but only files. Anything else
  // (text, including paste inside a code block) falls through to the editor.
  function onPasteCapture(e: React.ClipboardEvent) {
    const files = clipboardFiles(e);
    if (!files || !onPasteFiles) return;
    e.preventDefault();
    e.stopPropagation();
    onPasteFiles(files);
  }

  return (
    <div onKeyDownCapture={onKeyDownCapture} onPasteCapture={onPasteCapture}>
      {rawFallback ? (
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => {
            dirty.current = true;
            setDraft(e.target.value);
          }}
          rows={12}
          className="themed-scroll w-full resize-y rounded border border-blue-400 px-2 py-1.5 text-sm text-slate-900 outline-none ring-2 ring-blue-100"
        />
      ) : (
        <div className="overflow-hidden rounded border border-blue-400 ring-2 ring-blue-100">
          {/* the editor is a lazy chunk; after a deploy an old tab can't load
              it, and without a boundary that throw blanks the whole app */}
          <ErrorBoundary fallback={(e) => <ReloadNotice error={e} compact />}>
          <Suspense
            fallback={
              <div className="px-3 py-6 text-sm text-slate-400">
                Loading editor…
              </div>
            }
          >
            <MarkdownEditor
              ref={editorRef}
              markdown={value}
              onChange={(_md, initialNormalize) => {
                if (!initialNormalize) dirty.current = true;
              }}
              onUnrepresentable={() => {
                // Preserve the raw markdown and let the user edit it plainly.
                setDraft(value);
                setRawFallback(true);
              }}
            />
          </Suspense>
          </ErrorBoundary>
        </div>
      )}
      <div className="mt-1 flex items-center gap-2 text-xs">
        <button
          type="button"
          onClick={commit}
          className="rounded bg-blue-600 px-2 py-1 text-white hover:bg-blue-700"
        >
          Save
        </button>
        <button
          type="button"
          onClick={cancel}
          className="text-slate-500 hover:underline"
        >
          Cancel
        </button>
        <span className="ml-auto text-slate-400">
          {rawFallback
            ? "Markdown supported"
            : "Paste markdown · ⌘/Ctrl+Enter to save"}
        </span>
      </div>
    </div>
  );
}
